import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, CheckCircle2, AlertTriangle, XCircle, ArrowRight, Mail, Info } from 'lucide-react';

type SurchargeType = 'contact' | 'tunnel';

type CoverageResult = {
  status: 'standard' | 'surcharge' | 'outside' | 'invalid';
  poststed?: string;
  postnummer?: string;
  surchargeType?: SurchargeType;
};

const COMPANY_EMAIL = 'bestilling@bmgas.no';

// Askøy — kjøretillegg (kontakt for pris)
const ASKOY_SURCHARGE = new Set([
  '5307', // Ask
  '5311', // Kjerrgarden
  '5314', // Kjerrgarden
  '5315', // Herdla
  '5326', // Ask postboks
  '5328', // Herdla postboks
]);

// Sotra/Øygarden — kjøretillegg (kontakt for pris)
// TODO: Denne listen er et estimat basert på postnummerdata og bør dobbeltsjekkes med BMG.
const SOTRA_OYGARDEN_SURCHARGE = new Set([
  '5331', // Rong
  '5333', // Tjeldstø
  '5334', // Hellesøy
  '5335', // Hernar
  '5336', // Tjeldstø
  '5337', // Rong
  '5341', // Straume postboks
  '5342', // Straume postboks
  '5343', // Straume postboks
  '5344', // Straume postboks
  '5345', // Straume postboks
  '5346', // Ågotnes
  '5347', // Ågotnes
  '5363', // Ågotnes
  '5365', // Turøy
  '5366', // Misje
  '5371', // Skogsvåg
  '5373', // Skogsvåg
  '5374', // Steinsland
  '5378', // Klokkarvik
  '5379', // Steinsland
  '5380', // Telavåg
  '5381', // Glesvær
  '5382', // Skogsvåg
  '5077', // Bjørøyhamn
  '5177', // Bjørøyhamn
]);

// Os/Bjørnafjorden — fast tunnelttillegg 129 kr per vei
const OS_BJORNAFJORDEN_SURCHARGE = new Set([
  '5200', // Os
  '5201', // Os
  '5202', // Os
  '5203', // Os
  '5206', // Os
  '5207', // Søfteland
  '5208', // Os
  '5209', // Os
  '5210', // Os
  '5211', // Os
  '5212', // Søfteland
  '5213', // Lepsøy
  '5215', // Lysekloster
  '5216', // Lepsøy
  '5217', // Hagavik
  '5218', // Nordstrøno
  '5640', // Eikelandsosen
  '5641', // Fusa
  '5642', // Holmefjord
  '5643', // Strandvik
  '5644', // Sævareid
  '5645', // Sævareid
  '5646', // Nordtveitgrend
  '5647', // Baldersheim
  '5648', // Fusa
  '5649', // Eikelandsosen
]);

function getSurchargeType(postnummer: string): SurchargeType | null {
  if (ASKOY_SURCHARGE.has(postnummer)) return 'contact';
  if (SOTRA_OYGARDEN_SURCHARGE.has(postnummer)) return 'contact';
  if (OS_BJORNAFJORDEN_SURCHARGE.has(postnummer)) return 'tunnel';
  return null;
}

export default function CoverageChecker() {
  const [postalCode, setPostalCode] = useState('');
  const [result, setResult] = useState<CoverageResult | null>(null);
  const [loading, setLoading] = useState(false);

  const checkCoverage = async () => {
    const trimmed = postalCode.trim();
    if (!/^\d{4}$/.test(trimmed)) {
      setResult({ status: 'invalid' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/dekningsomrade.json');
      const data: Array<{ postnummer: string; poststed: string; kategori: string }> = await res.json();
      const entry = data.find((d) => d.postnummer === trimmed);
      if (!entry) {
        setResult({ status: 'outside', postnummer: trimmed });
      } else {
        const surchargeType = getSurchargeType(trimmed);
        if (surchargeType) {
          setResult({ status: 'surcharge', poststed: entry.poststed, postnummer: trimmed, surchargeType });
        } else {
          setResult({ status: 'standard', poststed: entry.poststed, postnummer: trimmed });
        }
      }
    } catch {
      setResult({ status: 'invalid' });
    } finally {
      setLoading(false);
    }
  };

  const isCovered = result?.status === 'standard' || result?.status === 'surcharge';

  return (
    <div className="mx-auto p-3 sm:p-4 rounded-2xl bg-white border border-bmg-gray-border shadow-sm w-full" style={{ maxWidth: 'min(840px, 100%)' }}>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <label htmlFor="postal-code-input" className="sr-only">Sjekk om vi leverer til ditt postnummer</label>
        <input
          id="postal-code-input"
          type="text"
          inputMode="numeric"
          maxLength={4}
          aria-label="Sjekk om vi leverer til ditt postnummer"
          className="input-bmg flex-1 sm:flex-none sm:w-[320px] text-lg sm:text-xl py-3.5 sm:py-4"
          placeholder="Skriv inn postnummer"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
          onKeyDown={(e) => e.key === 'Enter' && checkCoverage()}
        />
        <button
          onClick={checkCoverage}
          disabled={loading}
          className="btn-primary whitespace-nowrap text-base sm:text-lg py-3.5 sm:py-4"
        >
          <Search className="h-5 w-5" />
          Sjekk dekning
        </button>
      </div>

      {result && result.status !== 'invalid' && (
        <div className="mt-3 pt-3 border-t border-bmg-gray-border animate-[fadeIn_0.3s_ease-in-out]">
          <div
            className={`flex items-start gap-3 text-sm p-3 rounded-xl mb-3 ${
              result.status === 'standard'
                ? 'bg-green-50 border border-green-200'
                : result.status === 'surcharge'
                ? 'bg-amber-50 border border-amber-200'
                : 'bg-bmg-gray border border-bmg-gray-border'
            }`}
          >
            {result.status === 'standard' && <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />}
            {result.status === 'surcharge' && <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />}
            {result.status === 'outside' && <XCircle className="h-5 w-5 text-bmg-ink-soft flex-shrink-0 mt-0.5" />}
            <p
              className={
                result.status === 'standard'
                  ? 'text-green-800'
                  : result.status === 'surcharge'
                  ? 'text-amber-800'
                  : 'text-bmg-ink-soft'
              }
            >
              {result.status === 'standard' && `Vi leverer til ${result.poststed} — ingen kjøretillegg.`}
              {result.status === 'surcharge' && result.surchargeType === 'contact' && `Vi leverer til ${result.poststed}, men kjøretillegg kan tilkomme for dette området. Kontakt oss for pris.`}
              {result.status === 'surcharge' && result.surchargeType === 'tunnel' && `Vi leverer til ${result.poststed}. Tunnelltillegg 129 kr per vei tilkommer.`}
              {result.status === 'outside' && 'Vi dekker foreløpig ikke dette området direkte — ta kontakt så ser vi hva vi kan få til.'}
            </p>
          </div>

          {result.status === 'surcharge' && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                {result.surchargeType === 'tunnel'
                  ? 'Tunnelltillegget legges automatisk til i bestillingen. Du vil se totalbeløpet før du betaler.'
                  : 'Kjøretillegget avhenger av avstand og tilgjengelighet. Ta kontakt før bestilling for å få en pris på tillegget.'}
              </p>
            </div>
          )}

          {isCovered ? (
            <Link
              to={`/bestill?postnummer=${result.postnummer}`}
              className="btn-primary w-full justify-center text-base"
            >
              Bestill nå
              <ArrowRight className="h-5 w-5" />
            </Link>
          ) : (
            <a
              href={`mailto:${COMPANY_EMAIL}?subject=${encodeURIComponent(`Spørsmål om levering til ${result.postnummer}`)}`}
              className="btn-secondary w-full justify-center text-base"
            >
              <Mail className="h-5 w-5" />
              Spør om tilgjengelighet
            </a>
          )}
        </div>
      )}

      {result && result.status === 'invalid' && (
        <p className="text-sm text-red-600 mt-2 text-center animate-[fadeIn_0.3s_ease-in-out]">
          Vennligst skriv inn et gyldig 4-sifret postnummer.
        </p>
      )}
    </div>
  );
}
